import {
  PortalArticleBlock,
  PortalArticleSection,
  PortalArticleSectionContentItem,
  PortalArtigoModel,
  PortalSummaryTopic,
} from '../../../models/PortalArtigoModel'

export type PortalAnchorEntry = { value: string; label: string }
export type PortalPillarIndexEntry = {
  key: string
  title: string
  href: string
  isIntroduction: boolean
}

export function createPortalAnchor(text: string, fallback = 'secao') {
  const normalizedText = text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
  return normalizedText || fallback
}

export function ensureSummaryHref(anchor: string) {
  const cleanAnchor = anchor.trim().replace(/^#+/, '')
  return cleanAnchor ? `#${cleanAnchor}` : '#'
}

export function normalizePortalAnchorValue(value?: string | null) {
  return (value || '').trim().replace(/^#+/, '')
}

export function getSectionSummaryAnchor(section: PortalArticleSection) {
  return section.anchorId || section.id || createPortalAnchor(section.title)
}

export function collectPortalAnchorEntries(
  artigo: Pick<
    PortalArtigoModel,
    'conteudo' | 'faq' | 'cursosRelacionados' | 'artigosRelacionados'
  >
) {
  const entries: PortalAnchorEntry[] = []
  const add = (value: string | undefined, label: string) => {
    if (value) entries.push({ value, label })
  }

  ;(artigo.conteudo || []).forEach((block, blockIndex) => {
    if (block.type === 'intro') {
      collectSectionAnchors(block.section, `Introdução ${blockIndex + 1}`, add)
      return
    }
    if (block.type !== 'topic') return

    block.items.forEach((item, itemIndex) => {
      const label = `Tópico ${blockIndex + 1} / item ${itemIndex + 1}`
      if (item.type === 'image') {
        add(item.anchorId, `${label} / imagem`)
      } else if (item.type === 'section') {
        collectSectionAnchors(item.section, `${label} / ${item.role === 'main' ? 'H2' : 'H3'}`, add)
      }
    })
  })

  add(artigo.faq?.anchorId, 'FAQ')
  add(artigo.faq?.sectionId, 'FAQ')
  add(artigo.cursosRelacionados?.anchorId, 'Cursos relacionados')
  add(artigo.artigosRelacionados?.anchorId, 'Artigos relacionados')
  return entries
}

function collectSectionAnchors(
  section: PortalArticleSection,
  label: string,
  add: (value: string | undefined, label: string) => void
) {
  add(section.anchorId || section.id, label)
  ;(section.extraAnchorIds || []).forEach((anchorId, index) => add(anchorId, `${label} / extra ${index + 1}`))
  ;(section.content || []).forEach((item, index) => collectContentAnchors(item, `${label} / conteúdo ${index + 1}`, add))
}

function collectContentAnchors(
  item: PortalArticleSectionContentItem,
  label: string,
  add: (value: string | undefined, label: string) => void
) {
  if (item.type === 'bullets') {
    ;(item.anchorIds || []).forEach((anchorId, index) => add(anchorId, `${label} / marcador ${index + 1}`))
  }
  if (item.type === 'note') add(item.anchorId, `${label} / nota`)
  if (item.type === 'image') add(item.anchorId, `${label} / imagem`)
}

export function findDuplicatePortalAnchors(
  artigo: Pick<
    PortalArtigoModel,
    'conteudo' | 'faq' | 'cursosRelacionados' | 'artigosRelacionados'
  >
) {
  const seen = new Map<string, PortalAnchorEntry>()
  const duplicates: PortalAnchorEntry[] = []
  collectPortalAnchorEntries(artigo).forEach((entry) => {
    const normalized = normalizePortalAnchorValue(entry.value)
    if (!normalized) return
    if (seen.has(normalized)) duplicates.push(entry)
    else seen.set(normalized, entry)
  })
  return duplicates
}

export function hasPortalAnchor(
  artigo: Pick<
    PortalArtigoModel,
    'conteudo' | 'faq' | 'cursosRelacionados' | 'artigosRelacionados'
  >,
  href?: string
) {
  const normalizedHref = normalizePortalAnchorValue(href)
  if (!normalizedHref) return false
  return collectPortalAnchorEntries(artigo).some(
    (entry) => normalizePortalAnchorValue(entry.value) === normalizedHref
  )
}

export function generateSummaryTopicsFromBlocks(blocks: PortalArticleBlock[]) {
  const topics: PortalSummaryTopic[] = []
  blocks.forEach((block) => {
    if (block.type !== 'topic') return
    const main = block.items.find((item) => item.type === 'section' && item.role === 'main')
    if (!main || main.type !== 'section' || !main.section.title) return

    const topic: PortalSummaryTopic = {
      title: main.section.title,
      href: ensureSummaryHref(getSectionSummaryAnchor(main.section)),
      subtopics: block.items
        .filter((item) => item.type === 'section' && item.role === 'subtopic' && item.section.title)
        .map((item) => ({
          title: item.type === 'section' ? item.section.title : '',
          href: item.type === 'section' ? ensureSummaryHref(getSectionSummaryAnchor(item.section)) : '#',
        })),
    }
    topics.push(topic)
  })

  return topics
}

export function removeSummaryReferencesForAnchors(
  blocks: PortalArticleBlock[],
  anchors: Array<string | undefined>
): PortalArticleBlock[] {
  const removedAnchors = new Set(anchors.map(normalizePortalAnchorValue).filter(Boolean))
  if (removedAnchors.size === 0) return blocks

  return blocks.map((block) => {
    if (block.type !== 'summary') return block
    return {
      ...block,
      topics: block.topics
        .filter((topic) => !removedAnchors.has(normalizePortalAnchorValue(topic.href)))
        .map((topic) => ({
          ...topic,
          subtopics: topic.subtopics?.filter(
            (subtopic) => !removedAnchors.has(normalizePortalAnchorValue(subtopic.href))
          ),
        })),
    }
  })
}

export function getPortalPillarIndexEntries(
  blocks: PortalArticleBlock[]
): PortalPillarIndexEntry[] {
  const identities = new Map<string, { key: string; isIntroduction: boolean }>()

  blocks.forEach((block) => {
    if (block.type === 'intro') {
      const anchor = normalizePortalAnchorValue(getSectionSummaryAnchor(block.section))
      if (anchor) {
        identities.set(anchor, {
          key: `intro:${block.id || block.section.id || anchor}`,
          isIntroduction: true,
        })
      }
      return
    }
    if (block.type !== 'topic') return
    block.items.forEach((item) => {
      if (item.type !== 'section') return
      const anchor = normalizePortalAnchorValue(getSectionSummaryAnchor(item.section))
      if (!anchor) return
      identities.set(anchor, {
        key: `section:${item.id || item.section.id || anchor}`,
        isIntroduction: false,
      })
    })
  })

  const summary = blocks.find((block) => block.type === 'summary')
  if (!summary || summary.type !== 'summary') return []

  return summary.topics.map((topic) => {
    const anchor = normalizePortalAnchorValue(topic.href)
    const identity = identities.get(anchor)
    return {
      key: identity?.key || `href:${anchor}`,
      title: topic.title,
      href: ensureSummaryHref(anchor),
      isIntroduction: identity?.isIntroduction || false,
    }
  })
}
